import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { formatPrice } from '../../lib/utils';

export default function AdminPrices() {
  const [prices, setPrices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [tlds, setTlds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ provider_id: '', tld: '', registration: '', renewal: '', transfer: '' });
  const [filterProvider, setFilterProvider] = useState('all');
  const [filterTld, setFilterTld] = useState('all');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [p, t, pr] = await Promise.all([api.getProviders(), api.getTlds(), api.getPrices()]);
      setProviders(p); setTlds(t); setPrices(pr);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      const data = {
        provider_id: form.provider_id,
        tld: form.tld,
        registration: form.registration ? parseFloat(form.registration) : null,
        renewal: form.renewal ? parseFloat(form.renewal) : null,
        transfer: form.transfer ? parseFloat(form.transfer) : null,
      };
      await api.createPrice(data);
      toast.success('Preis gespeichert');
      setDialogOpen(false);
      loadData();
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Preis löschen?')) return;
    try { await api.deletePrice(id); toast.success('Gelöscht'); loadData(); } catch (e) { toast.error(e.message); }
  };

  const providerMap = Object.fromEntries(providers.map(p => [p.id, p.name]));
  const filtered = prices.filter(p => {
    if (filterProvider !== 'all' && p.provider_id !== filterProvider) return false;
    if (filterTld !== 'all' && p.tld !== filterTld) return false;
    return true;
  });

  return (
    <div data-testid="admin-prices">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{fontFamily: 'Space Grotesk'}}>Preise</h1>
          <p className="text-sm text-muted-foreground mt-1">{prices.length} Preise insgesamt</p>
        </div>
        <Button onClick={() => { setForm({ provider_id: '', tld: '', registration: '', renewal: '', transfer: '' }); setDialogOpen(true); }} className="gap-2" data-testid="admin-crud-create-button">
          <Plus className="w-4 h-4" /> Neuer Preis
        </Button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <Select value={filterProvider} onValueChange={setFilterProvider}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Anbieter filtern" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Anbieter</SelectItem>
            {providers.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterTld} onValueChange={setFilterTld}>
          <SelectTrigger className="w-40"><SelectValue placeholder="TLD filtern" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle TLDs</SelectItem>
            {tlds.map(t => <SelectItem key={t.tld} value={t.tld}>{t.tld}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600" /></div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Anbieter</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">TLD</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground">Registrierung</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground">Verlängerung</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground">Transfer</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => (
                <tr key={p.id} className={`border-b border-slate-50 ${i % 2 === 0 ? '' : 'bg-slate-50/40'} hover:bg-sky-50/60 transition-colors`}>
                  <td className="px-4 py-3 font-medium">{providerMap[p.provider_id] || p.provider_id}</td>
                  <td className="px-4 py-3 font-bold">{p.tld}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatPrice(p.registration)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatPrice(p.renewal)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatPrice(p.transfer)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id)} className="text-red-500"><Trash2 className="w-3 h-3" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <div className="p-8 text-center text-muted-foreground">Keine Preise gefunden</div>}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Preis anlegen/aktualisieren</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Anbieter *</Label>
              <Select value={form.provider_id} onValueChange={v => setForm({...form, provider_id: v})}>
                <SelectTrigger><SelectValue placeholder="Anbieter wählen" /></SelectTrigger>
                <SelectContent>{providers.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>TLD *</Label>
              <Select value={form.tld} onValueChange={v => setForm({...form, tld: v})}>
                <SelectTrigger><SelectValue placeholder="TLD wählen" /></SelectTrigger>
                <SelectContent>{tlds.map(t => <SelectItem key={t.tld} value={t.tld}>{t.tld}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Registrierung</Label><Input type="number" step="0.01" value={form.registration} onChange={e => setForm({...form, registration: e.target.value})} placeholder="0.00" /></div>
              <div><Label>Verlängerung</Label><Input type="number" step="0.01" value={form.renewal} onChange={e => setForm({...form, renewal: e.target.value})} placeholder="0.00" /></div>
              <div><Label>Transfer</Label><Input type="number" step="0.01" value={form.transfer} onChange={e => setForm({...form, transfer: e.target.value})} placeholder="0.00" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={!form.provider_id || !form.tld} data-testid="admin-save-button">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
